import { useState } from "react";
import { CATEGORIES } from "@/lib/store";
import { ORDER_CATALOG, FIELD_ORDER, FIELD_LABELS, catalogEntry, fieldOptions, fitSpecs, fullName } from "@/lib/orderCatalog";
import { STOCK_IPHONES, stockIphoneSpec, ANY_STORAGES, OTHER } from "@/lib/stockCatalog";
import { ModelPicker } from "@/components/site/ModelPicker";
import { ChipGroup } from "./ChipGroup";

const input = "w-full rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-white/40";
const listOf = (category) => (category === "iphone" ? STOCK_IPHONES : (ORDER_CATALOG[category] || []).map((e) => e.name));
const allColors = (entry) => {
  const def = entry?.fields?.color;
  return def ? (Array.isArray(def) ? def : Object.values(def.map).flat()) : [];
};

// Mantém as escolhas válidas para o modelo; uma cor digitada em "Outra" é preservada.
const withSpecs = (entry, specs) => {
  const fitted = fitSpecs(entry, specs);
  if (specs.color && !allColors(entry).includes(specs.color)) fitted.color = specs.color;
  return { base_model: entry.name, specs: fitted, model: fullName(entry, fitted), storage: fitted.storage || "", color: fitted.color || "" };
};

const CatalogSpecs = ({ entry, specs, onPick }) => FIELD_ORDER.map((field) => {
  const opts = fieldOptions(entry, field, specs);
  if (!opts?.length) return null;
  const isColor = field === "color";
  return (
    <ChipGroup key={`${entry.name}-${field}`} label={isColor && entry.group?.startsWith("Apple Watch") ? "Cor da caixa" : FIELD_LABELS[field]} options={opts} value={specs[field] || ""} testid={`stock-spec-${field}`}
      other={isColor} placeholder="Digite a cor" onChange={(v) => onPick(field, v)} />
  );
});

const IphoneSpecs = ({ form, setForm, spec }) => (
  <>
    <ChipGroup key={`${form.model}-storage`} label="Armazenamento" options={spec.storages} value={form.storage} testid="stock-spec-storage"
      onChange={(storage) => setForm((f) => ({ ...f, storage }))} />
    <ChipGroup key={`${form.model}-color`} label="Cor" options={spec.colors} value={form.color} testid="stock-spec-color" other placeholder="Digite a cor"
      onChange={(color) => setForm((f) => ({ ...f, color }))} />
  </>
);

const FreeSpecs = ({ form, setForm }) => (
  <>
    <ChipGroup label="Armazenamento" options={ANY_STORAGES} value={form.storage} testid="stock-spec-storage" onChange={(storage) => setForm((f) => ({ ...f, storage }))} />
    <label className="block">
      <span className="mb-1.5 block text-xs text-zinc-400">Cor</span>
      <input data-testid="product-color-input" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} placeholder="Ex: Titânio Natural" className={input} />
    </label>
  </>
);

export const StockModelFields = ({ form, setForm }) => {
  const list = listOf(form.category);
  const entry = form.category === "iphone" ? null : catalogEntry(form.category, form.base_model);
  const picked = entry ? entry.name : list.includes(form.model) ? form.model : "";
  const [otherPicked, setOther] = useState(false);
  const other = otherPicked || (!picked && !!form.model);
  const spec = form.category === "iphone" && picked ? stockIphoneSpec(picked) : null;
  const label = CATEGORIES.find((c) => c.id === form.category)?.label;

  const pick = (name) => {
    if (name === OTHER) {
      setOther(true);
      setForm((f) => ({ ...f, model: "", base_model: "", specs: {}, storage: "", color: "" }));
      return;
    }
    setOther(false);
    const e = catalogEntry(form.category, name);
    if (e) return setForm((f) => ({ ...f, ...withSpecs(e, {}) }));
    const s = stockIphoneSpec(name);
    return setForm((f) => ({ ...f, model: name, base_model: "", specs: {}, storage: s?.storages.includes(f.storage) ? f.storage : s?.storages[0] || "", color: s?.colors.includes(f.color) ? f.color : "" }));
  };
  const pickSpec = (field, value) => setForm((f) => ({ ...f, ...withSpecs(entry, { ...f.specs, [field]: value }) }));

  return (
    <div data-testid="stock-model-fields" className="space-y-4">
      <div>
        <span className="mb-1.5 block text-xs text-zinc-400">Modelo</span>
        <ModelPicker testid="stock-model" value={other ? OTHER : picked} onChange={pick} options={[...list, OTHER]} selectOnly
          groupOf={(o) => (o === OTHER ? "Outro" : catalogEntry(form.category, o)?.group)} placeholder={`Toque para escolher o ${label}`} className={input} />
      </div>
      {other && (
        <input data-testid="product-model-input" required value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} placeholder="Digite o nome do modelo" className={input} />
      )}
      {entry && <CatalogSpecs entry={entry} specs={form.specs || {}} onPick={pickSpec} />}
      {spec && <IphoneSpecs form={form} setForm={setForm} spec={spec} />}
      {other && <FreeSpecs form={form} setForm={setForm} />}
    </div>
  );
};
